/* eslint-disable @eslint-community/eslint-comments/no-unlimited-disable */
/* eslint-disable */
//  This is a copy-paste component from react-admin exclude of adding selectedIds?.length > 0 check for BulkActionsToolbar

import { Table } from '@mui/material';
import classNames from 'classnames';
import difference from 'lodash/difference';
import union from 'lodash/union';
import { Identifier, RaRecord, sanitizeListRestProps, useListContext } from 'ra-core';
import * as React from 'react';
import {
  cloneElement,
  createElement,
  FC,
  isValidElement,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  BulkDeleteButton,
  DatagridBody,
  DatagridClasses,
  DatagridHeader,
  DatagridLoading,
  DatagridProps,
  DatagridRoot,
  ListNoResults,
  PureDatagridBody,
} from 'react-admin';

import { BulkActionsToolbar } from '@sh4res/bo-common/components/BulkActionsToolbar';

const DatagridContext = React.createContext<DatagridContextValue>({});

DatagridContext.displayName = 'DatagridContext';

type DatagridContextValue = {
  isRowExpandable?: (record: RaRecord) => boolean;
  expandSingle?: boolean;
};

const DatagridContextProvider = ({
  children,
  value,
}: {
  children: React.ReactNode;
  value: DatagridContextValue;
}): ReactElement => <DatagridContext.Provider value={value}>{children}</DatagridContext.Provider>;

const defaultBulkActionButtons = <BulkDeleteButton />;

export type CustomDatagridProps = DatagridProps & {
  showBulkActionsToolbar?: boolean;
  testId?: string;
};

/**
 * The Datagrid component renders a list of records as a table.
 * It is usually used as a child of the <List> and <ReferenceManyField> components.
 *
 * Props:
 *  - body
 *  - bulkActionButtons
 *  - children
 *  - empty
 *  - expand
 *  - header
 *  - hover
 *  - isRowExpandable
 *  - isRowSelectable
 *  - optimized
 *  - rowClick
 *  - rowSx
 *  - size
 *  - sx
 *
 * @example // Display all posts as a datagrid
 * const postRowSx = (record, index) => ({
 *     backgroundColor: record.nb_views >= 500 ? '#efe' : 'white',
 * });
 * export const PostList = () => (
 *     <List>
 *         <Datagrid rowSx={postRowSx}>
 *             <TextField source="id" />
 *             <TextField source="title" />
 *             <TextField source="body" />
 *             <EditButton />
 *         </Datagrid>
 *     </List>
 * );
 *
 * @example // Display all the comments of the current post as a datagrid
 * <ReferenceManyField reference="comments" target="post_id">
 *     <Datagrid>
 *         <TextField source="id" />
 *         <TextField source="body" />
 *         <DateField source="created_at" />
 *         <EditButton />
 *     </Datagrid>
 * </ReferenceManyField>
 *
 * @example // Usage outside of a <List> or a <ReferenceManyField>.
 *
 * const sort = { field: 'published_at', order: 'DESC' };
 *
 * export const MyCustomList = (props) => {
 *     const { data, total, isLoading } = useGetList(
 *         'posts',
 *         { pagination: { page: 1, perPage: 10 }, sort: sort }
 *     );
 *
 *     return (
 *         <Datagrid
 *             data={data}
 *             total={total}
 *             isLoading={isLoading}
 *             sort={sort}
 *             selectedIds={[]}
 *             setSort={() => {
 *                 console.log('set sort');
 *             }}
 *             onSelect={() => {
 *                 console.log('on select');
 *             }}
 *             onToggleItem={() => {
 *                 console.log('on toggle item');
 *             }}
 *         >
 *             <TextField source="id" />
 *             <TextField source="title" />
 *         </Datagrid>
 *     );
 * }
 */
export const Datagrid: FC<CustomDatagridProps> = React.forwardRef((props, ref) => {
  const {
    optimized = false,
    body = optimized ? PureDatagridBody : DatagridBody,
    header = DatagridHeader,
    children,
    className,
    empty = DefaultEmpty,
    expand,
    bulkActionButtons = defaultBulkActionButtons,
    showBulkActionsToolbar = true,
    hover,
    isRowSelectable,
    isRowExpandable,
    resource,
    rowClick,
    rowSx,
    rowStyle,
    size = 'small',
    sx,
    expandSingle = false,
    testId,
    ...rest
  } = props;

  const { sort, data, isLoading, onSelect, onToggleItem, selectedIds, setSort, total } =
    useListContext(props);

  const hasBulkActions = !!bulkActionButtons !== false;

  const contextValue = useMemo(
    () => ({ isRowExpandable, expandSingle }),
    [isRowExpandable, expandSingle],
  );

  const lastSelected = useRef<Identifier | null>(null);
  // A DOM-valid sibling of <Table> for content the header can't render itself,
  // since <thead>/<table> only accept table-structure children.
  const announcerContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedIds || selectedIds.length === 0) {
      lastSelected.current = null;
    }
  }, [JSON.stringify(selectedIds)]);

  // we manage row selection at the datagrid level to allow shift+click to select an array of rows
  const handleToggleItem = useCallback(
    (id: Identifier, event: React.MouseEvent | React.TouchEvent) => {
      const ids = data.map((record) => record.id);
      const lastSelectedIndex = ids.indexOf(lastSelected.current);
      const checked = (event.target as HTMLInputElement).checked;
      lastSelected.current = checked ? id : null;

      if (event.shiftKey && lastSelectedIndex !== -1) {
        const index = ids.indexOf(id);
        const idsBetweenSelections = ids.slice(
          Math.min(lastSelectedIndex, index),
          Math.max(lastSelectedIndex, index) + 1,
        );

        const newSelectedIds = checked
          ? union(selectedIds, idsBetweenSelections)
          : difference(selectedIds, idsBetweenSelections);

        onSelect(
          isRowSelectable
            ? newSelectedIds.filter((id: Identifier) =>
                isRowSelectable(data.find((record) => record.id === id)),
              )
            : newSelectedIds,
        );
      } else {
        onToggleItem(id);
      }
    },
    [data, isRowSelectable, onSelect, onToggleItem, selectedIds],
  );

  if (isLoading === true) {
    return (
      <DatagridLoading
        className={className}
        expand={expand}
        hasBulkActions={hasBulkActions}
        nbChildren={React.Children.count(children)}
        size={size}
      />
    );
  }

  /**
   * Once loaded, the data for the list may be empty. Instead of
   * displaying the table header with zero data rows,
   * the Datagrid displays the empty component.
   */
  if (data == null || data.length === 0 || total === 0) {
    if (empty) {
      return empty;
    }

    return null;
  }

  /**
   * After the initial load, if the data for the list isn't empty,
   * and even if the data is refreshing (e.g. after a filter change),
   * the datagrid displays the current data.
   */
  return (
    <DatagridContextProvider value={contextValue}>
      <DatagridRoot sx={sx} className={classNames(DatagridClasses.root, className)}>
        {showBulkActionsToolbar && bulkActionButtons !== false && selectedIds?.length > 0 ? (
          <BulkActionsToolbar selectedIds={selectedIds} testId={testId}>
            {isValidElement(bulkActionButtons) ? bulkActionButtons : defaultBulkActionButtons}
          </BulkActionsToolbar>
        ) : null}
        <div className={DatagridClasses.tableWrapper}>
          <div ref={announcerContainerRef} />
          <Table
            ref={ref}
            className={DatagridClasses.table}
            size={size}
            {...sanitizeRestProps(rest)}>
            {createOrCloneElement(
              header,
              {
                children,
                sort,
                data,
                hasExpand: !!expand,
                hasBulkActions,
                isRowSelectable,
                onSelect,
                resource,
                selectedIds,
                setSort,
                announcerContainerRef,
              },
              children,
            )}
            {createOrCloneElement(
              body,
              {
                expand,
                rowClick,
                data,
                hasBulkActions,
                hover,
                onToggleItem: handleToggleItem,
                resource,
                rowSx,
                rowStyle,
                selectedIds,
                isRowSelectable,
              },
              children,
            )}
          </Table>
        </div>
      </DatagridRoot>
    </DatagridContextProvider>
  );
});

const createOrCloneElement = (
  element: ReactElement | React.ComponentType,
  props: Record<string, unknown>,
  children: React.ReactNode,
) =>
  isValidElement(element)
    ? cloneElement(element as ReactElement, props, children)
    : createElement(element as React.ComponentType, props, children);

const injectedProps = [
  'isRequired',
  'setFilter',
  'setPagination',
  'limitChoicesToValue',
  'translateChoice',
  // Datagrid may be used as an alternative to SelectInput
  'field',
  'fieldState',
  'formState',
];

const sanitizeRestProps = <T extends Record<string, unknown>>(props: T) =>
  Object.keys(sanitizeListRestProps(props))
    .filter((propName) => !injectedProps.includes(propName))
    .reduce((acc, key) => ({ ...acc, [key]: props[key] }), {});

Datagrid.displayName = 'Datagrid';

const DefaultEmpty = <ListNoResults />;
